import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { useSelector } from "react-redux";
import { Navigate, Link } from "react-router-dom";
import { DatePicker, Input, Select, Button, Space } from "antd";
import {
  FilterOutlined,
  ReloadOutlined,
  CarOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FlagOutlined,
  DownloadOutlined,
  RightOutlined,
  FieldTimeOutlined,
  DeploymentUnitOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import TabelaAcompanhamento from "../../components/TabelaAcompanhamento";
import PainelTempoReal from "../../components/PainelTempoReal";
import acompanhamentoApi from "../../service/modules/acompanhamento.api";
import shipperService from "../../service/shipper.service";
import useRecargaPeriodica from "../../util/freight/useRecargaPeriodica";
import useCards, { contarFretes } from "./cards";
import useOpcoesDeFiltro, {
  OPCOES_DE_UF,
  OPCOES_DE_TIPO,
} from "./opcoes-de-filtro";
import {
  comAlerta,
  diasDeAtraso,
  STATUS_EM_TRANSITO,
  STATUS_ENTREGUE,
  STATUS_FINALIZADO,
} from "../../util/freight/acompanhamento-marcacoes";

const { RangePicker } = DatePicker;

/**
 * A página aberta se renova a cada 30 s: acompanha ocorrência, última posição e troca de status sem
 * transformar a tela aberta o dia todo em carga — são de 20 a 50 leituras por recarga de 8 fretes.
 */
const RECARGA_DA_LISTA_MS = 30 * 1000;

const FILTROS_VAZIOS = {
  periodo: null,
  numberSerial: "",
  plate: "",
  status: undefined,
  uidShipper: undefined,
  driverUid: undefined,
  origem: undefined,
  destino: undefined,
  freightOwnershipType: undefined,
};

const OPCOES_DE_STATUS = [
  { value: STATUS_EM_TRANSITO, label: "Em trânsito" },
  { value: STATUS_ENTREGUE, label: "Entregue" },
  { value: STATUS_FINALIZADO, label: "Finalizado" },
];

const RELATORIOS = [
  {
    titulo: "Linha do tempo do frete",
    texto: "Acompanhe todas as etapas e eventos de cada frete.",
    Icone: FieldTimeOutlined,
    tipo: "linha-do-tempo",
  },
  {
    titulo: "Visão consolidada de frota",
    texto: "Visão geral do status e desempenho da frota.",
    Icone: DeploymentUnitOutlined,
    tipo: "frota",
  },
  {
    titulo: "Relatório mensal por placa",
    texto: "Resumo mensal de viagens e desempenho por placa.",
    Icone: BarChartOutlined,
    tipo: "mensal",
  },
];

const CARDS = [
  { chave: "emTransito", rotulo: "Em trânsito", dica: "Viagens em andamento", tema: "transito", Icone: CarOutlined },
  { chave: "comAlerta", rotulo: "Com ocorrência", dica: "Ao menos uma das 11 ocorrências", tema: "alerta", Icone: ExclamationCircleOutlined, filtra: true },
  { chave: "emAtraso", rotulo: "Em atraso", dica: "Previsão de entrega vencida", tema: "atraso", Icone: ClockCircleOutlined },
  { chave: "entregues", rotulo: "Entregues", dica: "Entregas realizadas", tema: "entregue", Icone: CheckCircleOutlined },
  { chave: "finalizadas", rotulo: "Finalizadas", dica: "Viagens finalizadas", tema: "finalizada", Icone: FlagOutlined },
];

function Acompanhamento() {
  const user = useSelector((state) => state.user);
  const logado = useSelector((state) => state.usuarioLogado);

  const [soComAlerta, setSoComAlerta] = useState(false);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VAZIOS);
  const [fretes, setFretes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(8);
  const [hasMore, setHasMore] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  // Nulo quando não dá para afirmar: busca por placa ou por número não passa por contagem agregada,
  // e período pediria um índice a mais. Melhor não mostrar total do que mostrar total errado.
  const [total, setTotal] = useState(null);

  // Um cursor por página. Voltar não custa requisição nova de descoberta: o cursor da página
  // anterior já está guardado, como a tabela de fretes deles também faz.
  const cursoresRef = useRef(new Map());
  // Embarcadores que listam esta transportadora em affiliatedCarrierIds. O dono do frete
  // já entra pelo uid dele; estes uids fazem a afiliada enxergar o mesmo frete.
  const [clientesAfiliados, setClientesAfiliados] = useState([]);
  const [ehTransportadora, setEhTransportadora] = useState(false);

  useEffect(() => {
    let cancelado = false;
    const carrierUid = String(user?.uidShipper || "").trim();

    if (!carrierUid || user?.viewALL) {
      setClientesAfiliados([]);
      setEhTransportadora(false);
      return undefined;
    }

    (async () => {
      let transportadora = false;
      try {
        const snap = await shipperService.getShipperAvailable(carrierUid);
        transportadora =
          snap.exists && snap.data()?.isTransport === true;
      } catch (error) {
        console.error(
          "Erro ao identificar a transportadora no acompanhamento:",
          error,
        );
      }

      if (cancelado) return;
      if (!transportadora) {
        setClientesAfiliados([]);
        setEhTransportadora(false);
        return;
      }

      setEhTransportadora(true);

      try {
        const clientes =
          await shipperService.getClientEmbarcadoresByCarrierId(carrierUid);
        if (!cancelado) {
          setClientesAfiliados(
            clientes.map((item) => item.id).filter(Boolean),
          );
        }
      } catch (error) {
        console.error(
          "Erro ao carregar embarcadores afiliados da transportadora:",
          error,
        );
        if (!cancelado) setClientesAfiliados([]);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [user?.uidShipper, user?.viewALL]);

  const escopo = useMemo(() => {
    const afiliados = Array.isArray(user?.uidShipperAffiliates)
      ? user.uidShipperAffiliates
      : [];
    const proprio = user?.uidShipper ? [user.uidShipper] : [];
    const uids = Array.from(
      new Set([...afiliados, ...proprio, ...clientesAfiliados]),
    );

    return {
      viewALL: Boolean(user?.viewALL),
      uids,
      // `idUser` continua indo, mesmo depois de o escopo passar a sair do token: os PRs do painel e
      // do backend sao merges separados, e sem ele a tela nao lista nada contra o backend anterior.
      // Transportadora não manda uidShipper na listagem: esse parâmetro só estreita, e o
      // escopo ampliado (embarcadores afiliados) é resolvido no token. O recorte fica no filtro.
      params: user?.viewALL
        ? { idUser: user?.uid, viewAll: "true" }
        : ehTransportadora
          ? { idUser: user?.uid }
          : { uidShipper: uids },
    };
  }, [user, clientesAfiliados, ehTransportadora]);

  const cards = useCards(escopo);

  /**
   * O recorte é sobre a página carregada, e não sobre o período inteiro: alerta é calculado depois
   * da página, então filtrar por ele no cursor exigiria varrer a frota toda. É a mesma cobertura da
   * contagem do card, que já marca `+` quando há mais.
   */
  const fretesVisiveis = useMemo(
    () => (soComAlerta ? fretes.filter(comAlerta) : fretes),
    [fretes, soComAlerta],
  );
  const { embarcadores, motoristas, placas } = useOpcoesDeFiltro(escopo);

  // Conta as cargas que o operador pede. A recarga silenciosa guarda o número de quando saiu e se
  // descarta se outra carga começou no meio — é de uma página ou filtro que já não estão na tela. O
  // contrário não vale: recarga nenhuma derruba a carga pedida, senão o `carregando` ficaria ligado.
  const requisicaoRef = useRef(0);

  const carregar = useCallback(
    async (paginaAlvo, { silencioso = false } = {}) => {
      // Na primeira renderização o usuário ainda não está na store, e sair sem escopo é pedir uma
      // página que já nasce vazia. Esperar custa nada; a tela pinta os cards enquanto isso.
      if (!escopo.viewALL && !escopo.uids.length) return;

      const requisicao = silencioso
        ? requisicaoRef.current
        : ++requisicaoRef.current;
      if (!silencioso) setCarregando(true);
      try {
        const resposta = await acompanhamentoApi.listarFretes({
          ...escopo.params,
          // Escolher um embarcador estreita o escopo; nunca o amplia — quem não pode vê-lo não
          // recebe nada de volta, porque o backend resolve o escopo pelo usuário e não por isto.
          ...(filtrosAplicados.uidShipper
            ? { uidShipper: filtrosAplicados.uidShipper, idUser: undefined, viewAll: undefined }
            : {}),
          limit: tamanho,
          cursor: cursoresRef.current.get(paginaAlvo),
          status: filtrosAplicados.status,
          numberSerial: filtrosAplicados.numberSerial || undefined,
          plate: filtrosAplicados.plate || undefined,
          driverUid: filtrosAplicados.driverUid,
          origem: filtrosAplicados.origem,
          destino: filtrosAplicados.destino,
          freightOwnershipType: filtrosAplicados.freightOwnershipType,
          dataDe: filtrosAplicados.periodo?.[0]?.format("YYYY-MM-DD"),
          dataAte: filtrosAplicados.periodo?.[1]?.format("YYYY-MM-DD"),
        });

        if (requisicao !== requisicaoRef.current) return;

        const dados = resposta?.data?.data ?? [];
        const paginacao = resposta?.data?.pagination ?? {};

        if (paginacao.nextCursor) {
          cursoresRef.current.set(paginaAlvo + 1, paginacao.nextCursor);
        }

        setFretes(dados.map((f) => ({ ...f, _diasDeAtraso: diasDeAtraso(f) })));
        setHasMore(Boolean(paginacao.hasMore));
        setPagina(paginaAlvo);
        // Na recarga silenciosa o frete aberto no painel fica, mesmo que tenha saído da página: um
        // frete novo empurra a lista, e o mapa não pode trocar de viagem sozinho.
        setSelecionado(
          (atual) =>
            dados.find((f) => f.id === atual?.id) ??
            (silencioso && atual ? atual : dados[0]) ??
            null,
        );
      } catch (erro) {
        if (requisicao !== requisicaoRef.current) return;
        // Sem isto a tela renderizaria lista vazia sem dizer por quê — foi assim que a falha de
        // índice da tela antiga ficou invisível.
        console.error("Erro ao carregar os fretes do acompanhamento:", erro);
        // Recarga silenciosa que falha mantém a lista: zerar a cada queda de rede faria a tabela
        // piscar vazia de 30 em 30 s.
        if (silencioso) return;
        setFretes([]);
        setHasMore(false);
      } finally {
        if (!silencioso && requisicao === requisicaoRef.current) {
          setCarregando(false);
        }
      }
    },
    [escopo, tamanho, filtrosAplicados],
  );

  /**
   * O cursor só sabe avançar de uma página por vez — é o preço de paginar sem `offset`, e é o que
   * mantém o custo em 20 leituras por página em vez de crescer com a profundidade. Pular para uma
   * página distante caminha até ela a partir do cursor conhecido mais próximo.
   */
  const irParaPagina = useCallback(
    async (paginaAlvo) => {
      if (paginaAlvo < 1) return;
      if (paginaAlvo === 1 || cursoresRef.current.has(paginaAlvo)) {
        await carregar(paginaAlvo);
        return;
      }

      let anterior = paginaAlvo - 1;
      while (anterior > 1 && !cursoresRef.current.has(anterior)) anterior -= 1;
      for (let n = anterior; n < paginaAlvo; n += 1) {
        // eslint-disable-next-line no-await-in-loop
        await carregar(n);
      }
      await carregar(paginaAlvo);
    },
    [carregar],
  );

  useEffect(() => {
    cursoresRef.current = new Map();
    carregar(1);
  }, [carregar]);

  // A página aberta se renova sozinha, sem o `carregando` — que cobre a tabela e trava o paginador —
  // e nunca por cima de uma carga que o operador pediu.
  const recarregarPagina = useCallback(async () => {
    if (carregando) return;
    await carregar(pagina, { silencioso: true });
  }, [carregando, carregar, pagina]);

  useRecargaPeriodica(recarregarPagina, RECARGA_DA_LISTA_MS);

  useEffect(() => {
    const buscaDireta =
      filtrosAplicados.plate ||
      filtrosAplicados.numberSerial ||
      filtrosAplicados.periodo;
    if (buscaDireta || (!escopo.viewALL && !escopo.uids.length)) {
      setTotal(null);
      return;
    }

    let cancelado = false;
    contarFretes(escopo, filtrosAplicados.status)
      .then((snap) => {
        if (!cancelado) setTotal(snap.data().count);
      })
      .catch((erro) => {
        if (cancelado) return;
        setTotal(null);
        console.error("Erro ao contar os fretes do acompanhamento:", erro);
      });

    return () => {
      cancelado = true;
    };
  }, [escopo, filtrosAplicados]);

  const aplicarFiltros = () => setFiltrosAplicados(filtros);

  const limparFiltros = () => {
    setFiltros(FILTROS_VAZIOS);
    setFiltrosAplicados(FILTROS_VAZIOS);
  };

  const paginacao = {
    pagina,
    tamanho,
    hasMore,
    total,
    primeiro: fretes.length ? (pagina - 1) * tamanho + 1 : 0,
    ultimo: (pagina - 1) * tamanho + fretes.length,
  };

  const conteudo = (
    <div className="acomp">
      <header className="acomp__cabecalho">
        <h1>Acompanhamento de entregas</h1>
        <p>Acompanhe em tempo real o status das entregas e o histórico das operações.</p>
      </header>

      <div className="acomp__cards">
        {CARDS.map(({ chave, rotulo, dica, tema, Icone, filtra }) => {
          const Elemento = filtra ? "button" : "article";
          return (
            <Elemento
              key={chave}
              type={filtra ? "button" : undefined}
              onClick={filtra ? () => setSoComAlerta((v) => !v) : undefined}
              className={`acomp__card acomp__card--${tema}${
                filtra && soComAlerta ? " acomp__card--ativo" : ""
              }`}
            >
              <span className="acomp__card-icone" aria-hidden="true">
                <Icone />
              </span>
              <div className="acomp__card-texto">
                <span className="acomp__card-rotulo">{rotulo}</span>
                <strong className="acomp__card-valor">
                  {cards.alertaParcial && (chave === "comAlerta" || chave === "emAtraso")
                    ? `${cards[chave]}+`
                    : cards[chave]}
                </strong>
                <span className="acomp__card-dica">
                  {filtra && soComAlerta ? "Clique para ver todos" : dica}
                </span>
              </div>
            </Elemento>
          );
        })}
      </div>

      <section className="acomp__filtros">
        <div className="acomp__filtro">
          <label>Período de coleta</label>
          <RangePicker
            format="DD/MM/YYYY"
            value={filtros.periodo}
            onChange={(v) => setFiltros((f) => ({ ...f, periodo: v }))}
            allowEmpty={[true, true]}
          />
        </div>
        <div className="acomp__filtro">
          <label>Código do frete</label>
          <Input
            placeholder="Digite o código do frete"
            value={filtros.numberSerial}
            onChange={(e) =>
              setFiltros((f) => ({ ...f, numberSerial: e.target.value }))
            }
          />
        </div>
        <div className="acomp__filtro">
          <label>Embarcador</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todos"
            options={embarcadores}
            value={filtros.uidShipper}
            onChange={(v) => setFiltros((f) => ({ ...f, uidShipper: v }))}
          />
        </div>
        <div className="acomp__filtro">
          <label>Motorista</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todos"
            options={motoristas}
            value={filtros.driverUid}
            onChange={(v) => setFiltros((f) => ({ ...f, driverUid: v }))}
          />
        </div>
        <div className="acomp__filtro">
          <label>Placa</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todas"
            options={placas}
            value={filtros.plate || undefined}
            onChange={(v) => setFiltros((f) => ({ ...f, plate: v ?? "" }))}
          />
        </div>
        <div className="acomp__filtro">
          <label>Frete</label>
          <Select
            allowClear
            placeholder="Todos"
            options={OPCOES_DE_TIPO}
            value={filtros.freightOwnershipType}
            onChange={(v) =>
              setFiltros((f) => ({ ...f, freightOwnershipType: v }))
            }
          />
        </div>
        <div className="acomp__filtro">
          <label>Origem</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todas"
            options={OPCOES_DE_UF}
            value={filtros.origem}
            onChange={(v) => setFiltros((f) => ({ ...f, origem: v }))}
          />
        </div>
        <div className="acomp__filtro">
          <label>Destino</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Todos"
            options={OPCOES_DE_UF}
            value={filtros.destino}
            onChange={(v) => setFiltros((f) => ({ ...f, destino: v }))}
          />
        </div>
        <div className="acomp__filtro">
          <label>Status</label>
          <Select
            allowClear
            placeholder="Todos"
            options={OPCOES_DE_STATUS}
            value={filtros.status}
            onChange={(v) => setFiltros((f) => ({ ...f, status: v }))}
          />
        </div>
        <div className="acomp__filtro-acoes">
          <Space size={8}>
            <Button type="primary" icon={<FilterOutlined />} onClick={aplicarFiltros}>
              Filtrar
            </Button>
            <Button icon={<ReloadOutlined />} onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </Space>
        </div>
      </section>

      <div className="acomp__conteudo">
        <TabelaAcompanhamento
          fretes={fretesVisiveis}
          carregando={carregando}
          paginacao={paginacao}
          freteSelecionado={selecionado}
          onSelecionar={setSelecionado}
          onMudarPagina={irParaPagina}
          onMudarTamanhoDaPagina={setTamanho}
        />
        <PainelTempoReal frete={selecionado} />
      </div>

      <section className="acomp__relatorios">
        <header className="acomp__relatorios-cabecalho">
          <div>
            <h2>Relatórios</h2>
            <p>Gere relatórios detalhados para análise e acompanhamento da sua operação.</p>
          </div>
          <Link to="/acompanhamento/relatorios">
            <Button icon={<DownloadOutlined />}>Gerar relatórios</Button>
          </Link>
        </header>
        <div className="acomp__relatorios-lista">
          {RELATORIOS.map(({ titulo, texto, Icone, tipo }) => (
            <Link key={titulo} className="acomp__relatorio" to={`/acompanhamento/relatorios?tipo=${tipo}`}>
              <span className="acomp__relatorio-icone" aria-hidden="true">
                <Icone />
              </span>
              <div className="acomp__relatorio-texto">
                <strong>{titulo}</strong>
                <span>{texto}</span>
              </div>
              <RightOutlined className="acomp__relatorio-seta" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );

  return logado > 0 ? <NewMiniDrawer divOpen={conteudo} /> : <Navigate to="/login" />;
}

export default Acompanhamento;
